'use strict';

/**
 * The ONE registry of SYSTEM ACTORS — the identities a non-human writer records where a user id would go (an audit
 * row's `userId`, a cross-app relay token's `sub`, a job's `actorUserId`).
 *
 * REPAIR-LEDGER-AND-TYPED-ID-001 T3 S1. The census of 2026-09-26 found the actor strings hand-typed across ten
 * apps and five packages, in two spellings (`system-x`, `system:x`). A system actor is not an entity and never gets
 * an EPI id; it is a DECLARED NAME, and the typed ID check accepts exactly these names as sentinels on the columns
 * they land in. Owner ruling 2026-09-26: "Sentinels + fix writers (Recommended)".
 *
 * **Naming:** `system:<purpose>`. Two values keep their historical spelling because hash-chained audit history
 * records them and is never rewritten (decision D-1): `system` (the generic actor) and `migration-agent` (the
 * PostalMate migration agent's; 21,385 audit rows in D Town — also the actor of ProspectPuffin's source-migration
 * operator CLIs since R-180).
 *
 * @module @puffin-core/id-generator/systemActors
 */

/**
 * @type {Readonly<Object<string, string>>}
 */
const SYSTEM_ACTORS = Object.freeze({
  /** Generic system actor — history-pinned spelling. */
  SYSTEM: 'system',
  /**
   * An AI tool registry (`@puffin-core/tool-registry`) recording the tool surface it holds — every skill it registers
   * at boot and every one it removes (#47). port-api's AI receptionist registry wrote these rows with NO actor, so the
   * writer refused every one and the surface a SOC-2 reviewer reconstructs from them was never recorded.
   */
  AI_TOOL_REGISTRY: 'system:ai-tool-registry',
  /**
   * A MIGRATION's operator actor — history-pinned spelling. The PostalMate migration agent, and ProspectPuffin's
   * source-migration operator CLIs (PROSPECT-SRCMIG-001 R-180): a CLI states it ONCE, on the bound audit facade it
   * writes through, with `metadata.cli` naming the command. Route and job facades stay UNBOUND — an event there
   * carries its own actor, and one without is refused (#47).
   */
  MIGRATION_AGENT: 'migration-agent',
  /**
   * The actor of an UNAUTHENTICATED request (a denied report view, an anonymous comment attempt) — not a system
   * process, but a declared non-entity actor name all the same.
   */
  ANONYMOUS: 'anonymous',
  /**
   * planner-api's approval-escalation automation (#47): the SLA sweeps and the auto-escalation of a leave item no
   * present approver may decide. It was hand-typed as `system_escalation_sweep`, outside this registry, so the
   * typed-actor writer refused every automatic escalation's audit row.
   */
  APPROVAL_ESCALATION: 'system:approval-escalation',
  /**
   * A producer app's app-event dispatcher (`@puffin-core/app-events`, CROSS-APP-EVENTS-001): the subject of the
   * cross-app token every delivery to a consumer's inbox carries. It delivers what a write recorded; no principal is
   * signed in when the after-commit kick or the dispatch sweep sends it.
   */
  APP_EVENTS_DISPATCH: 'system:app-events-dispatch',
  /**
   * An operator command that changes a company's apps outside the console (PLATFORM-STAFF-IDEMPOTENCY-001): the e2e
   * seed's complimentary grants, an operator's removal. A platform change names exactly one actor; when no staff member
   * makes it, this is its system actor, and the run's command rides `metadata.operatorCommand`.
   */
  APP_ENTITLEMENT_OPERATOR: 'system:app-entitlement-operator',
  BROADCAST_EXPIRY_SWEEP: 'system:broadcast-expiry-sweep',
  /**
   * A CPaaS carrier's callback into port-api — an inbound message, a delivery status, a porting status (#47): the
   * carrier acts, no principal is signed in, and the row's actor is this declared name.
   */
  CPAAS_WEBHOOK: 'system:cpaas-webhook',
  /**
   * A DATA REPAIR — a migration that corrects or annotates recorded data (TENANT-ID-ORPHAN-REPAIR-001: the audit
   * annotations naming the tenant a mis-partitioned row belonged to). The repair ledger records WHICH run made a
   * change (`migrationName` / `runLabel`); this names the actor where a row must name one.
   */
  DATA_REPAIR: 'system:data-repair',
  /** The dev-only synthetic tour-event emitter (never mounted outside development). */
  DEV_TEST: 'system:dev-test',
  EMERGENCY: 'system:emergency',
  /**
   * port-api's inbound-fax email-RETRY job (the BullMQ `faxEmailDelivery` worker): it re-sends a received fax to the
   * routing rule's mailbox after the webhook's own attempt failed (#47). A job, not the carrier: the webhook's attempt
   * is `CPAAS_WEBHOOK`, the retry's is this.
   */
  FAX_EMAIL_RETRY: 'system:fax-email-retry',
  /** pos's sweep that expires each held sale past its store's close (HELD-SALES-001 B8). */
  HELD_SALE_EXPIRY_SWEEP: 'system:held-sale-expiry-sweep',
  /**
   * port-api's secret-gated internal transfer relay (`/v1/port/_internal/transfers`) — the principal its idempotency
   * guard scopes by. It was hand-typed as `internal-transfer`, outside this registry (typed-actor C2, 2026-09-26).
   */
  INTERNAL_TRANSFER: 'system:port-internal-transfer',
  /**
   * port-api's secret-gated internal AI debug surface (`/v1/port/_internal/ai` — replay, synthetic utterance, force-end)
   * (#47). The router authenticates its caller ONLY by the shared `X-Port-Internal-Secret`: no token, so no subject to
   * name. This is the one identity it verifies — a holder of port's internal secret driving that surface.
   */
  PORT_INTERNAL_AI_DEBUG: 'system:port-internal-ai-debug',
  /**
   * planner's relay of an invitation lifecycle moment to ParcelPuffin, which alone sends invitation notices (RLT-Q42):
   * the subject of the cross-app token; the person who acted travels in the body.
   */
  INVITATION_NOTICES: 'system:invitation-notices',
  MAIL_ARRIVAL: 'system:mail-arrival',
  /**
   * pos's daily charge of a saved card for a mailbox rental's renewal under the holder's autopay authorization
   * (MAILBOX-RENT-PAYMENTS-001 U12): the actor of the attempt row it opens and of the rent sale its webhook settles.
   */
  MAILBOX_AUTOPAY_SWEEP: 'system:mailbox-autopay-sweep',
  /**
   * purchase-api's relay of a mailbox holder's ONLINE rent payment to pos (MAILBOX-RENT-PAYMENTS-001 U4): the subject
   * of the cross-app token and the actor of the rent sale pos records; the holder travels in the body.
   */
  MAILBOX_ONLINE_PAYMENT: 'system:mailbox-online-payment',
  /** pos's hourly mark of a rented box whose rental's renewal date has passed at its store (L-R7CLOSE row 54). */
  MAILBOX_PAST_DUE_SWEEP: 'system:mailbox-past-due-sweep',
  /**
   * pos's daily rent reminders, overdue notices and late-fee assessments over the rentals due at a store
   * (MAILBOX-RENT-PAYMENTS-001 U12; owner ruling R-MRP-2): the actor every assessed fee names.
   */
  MAILBOX_RENT_NOTICES_SWEEP: 'system:mailbox-rent-notices-sweep',
  MARKETING_BLOG_SYNC: 'system:marketing-blog-sync',
  MARKETING_CMS_RETENTION_GC: 'system:marketing-cms-retention-gc',
  MARKETPLACE: 'system:marketplace',
  OUTBOUND_DISPATCH: 'system:outbound-dispatch',
  /**
   * port-api's Grafana / Alertmanager receiver paging the on-call admin by SMS (`@puffin-core/ops-alerting`): the page
   * leaves from the platform number (owner ruling 2026-10-06 "Platform fallback number"), whose audit row needs an actor
   * and has no signed-in principal — the receiver authenticates Grafana only by its shared secret.
   */
  OPS_ALERT_PAGE: 'system:ops-alert-page',
  /**
   * The register's cancel of a PurchasePuffin order, relayed pos → purchase (REGISTER-WEB-ORDERS-001 B7b): its token's
   * subject — the cashier is named in the relay's BODY and purchase audits under them, so this is the relay, never the
   * person (CROSS-APP-RELAY-001).
   */
  ORDER_CANCEL_RELAY: 'system:order-cancel-relay',
  /**
   * palm's dashboard reading a store's day from pos on a viewer's behalf (PALM-PHONE-UAT-001 C9, owner ruling R-4):
   * its token's fallback identity — every read names the viewer, so a request that names no one is the defect this marks.
   */
  PALM_DASHBOARD: 'system:palm-dashboard',
  PAYDAY_MINUTE_REPORTER: 'system:port-payday-minute-reporter',
  /**
   * The platform Leads inbox's cross-app client (purchase → pos funnel erasure, N167): its token's fallback identity —
   * every erasure names the acting platform staff member, so a request that names no one is the defect this marks.
   */
  PLATFORM_LEADS: 'system:platform-leads',
  PORT_ACTIVITY: 'system:port-activity',
  /**
   * port-api's live call pipeline acting on its own — the missed-call auto-text, the AI receptionist's skills, call
   * routing's hand-offs (#47): a caller on the line is not a signed-in principal.
   */
  PORT_CALL_PIPELINE: 'system:port-call-pipeline',
  PORT_CROSS_APP: 'system:port-cross-app',
  /**
   * port-api's job that tells pos when a customer was last heard from on the phone (`PosCustomerLastContactUpdaterJob`
   * → pos `_internal/customers/:id/port-last-activity`): the subject of its relay token. It was hand-typed as
   * `svc_port_last_activity_updater`, outside this registry, so the relay-only verifier refuses it by name
   * (CROSS-APP-RELAY-001).
   */
  PORT_LAST_ACTIVITY: 'system:port-last-activity',
  // pos's operational dashboard reading a sibling on a viewer's behalf (its token's fallback identity).
  POS_DASHBOARD: 'system:pos-dashboard',
  /**
   * pos-api's Store Profile reading and adding images in the store's site-media library through purchase-api
   * (`PurchaseSiteMediaClient` → purchase `_internal/site-media`, TENANT-LOGO): the subject of its relay token. The
   * person who chose or uploaded the image is pos-api's (its `SETTINGS.MANAGE` gate and its audit of the save).
   */
  POS_STORE_PROFILE: 'system:pos-store-profile',
  // POS-PRINT-LAYOUTS-001 M1.9: a store's paired print agent acting on its own token (pairing, a job's printed / failed
  // ack); the audit metadata names the agent (`agentId`), the station and, on pairing, who asked for the code.
  PRINT_AGENT: 'system:print-agent',
  PROSPECT_OUTREACH: 'system:prospect-outreach',
  PROVISIONING: 'system:provisioning',
  PUBLISHPUFFIN_SYNC: 'system:publishpuffin-sync',
  PURCHASE_STOREFRONT: 'system:purchase-storefront',
  /**
   * pos-api's scheduled drop of expired restore targets (a DigitalOcean database fork) — the actor of the platform
   * audit row. It was hand-typed as `restore-target-gc`, outside this registry (#47).
   */
  RESTORE_TARGET_GC: 'system:restore-target-gc',
  /** pos-api's scheduled restore-verification drill (hand-typed as `restore-verification-drill` until #47). */
  RESTORE_VERIFICATION_DRILL: 'system:restore-verification-drill',
  REVOKE_CASCADE: 'system:revoke-cascade',
  SCHEDULER: 'system:scheduler',
  /**
   * The integration factory's leaked-partition sweep (`@puffin-core/test-isolation`), as the attributed writer that
   * removes a killed suite's test-fixture company value from a feature flag (TENANT-ID-ORPHAN-FOLLOWON-001). It only
   * ever removes a value keyed to a `LEAKED_PARTITION_PATTERN` tenant.
   */
  TEST_PARTITION_SWEEP: 'system:test-partition-sweep',
  /** A Stripe webhook delivery (pos billing, purchase payments) — the event's actor, where there is no principal. */
  STRIPE_WEBHOOK: 'system:stripe-webhook',
  TIMECARDS: 'system:timecards',
  TIME_OFF: 'system:timeoff',
  /**
   * pos-api's nightly trial-expiry sweep (USER-FACING-GEMS LEAD-UFG-B): the actor that moves a company whose trial
   * has ended to `subscriptionStatus = 'expired'` — a platform decision, never a Stripe event.
   */
  TRIAL_EXPIRY_SWEEP: 'system:trial-expiry-sweep',
  // USER-FACING-GEMS LEAD-UFG-A follow-up (2026-09-30): the stored-card-trial reconcile sweep — creates the
  // Stripe Subscription a stored-card trial should have had; audited under its own name, never `scheduler`.
  STORED_CARD_TRIAL_RECONCILE: 'system:stored-card-trial-reconcile',
});

const ACTOR_NAMES = new Set(Object.values(SYSTEM_ACTORS));

/**
 * Is this value a declared system actor?
 *
 * @param {unknown} value - A candidate actor id.
 * @returns {boolean}
 */
function isSystemActor(value) {
  return typeof value === 'string' && ACTOR_NAMES.has(value);
}

module.exports = { SYSTEM_ACTORS, isSystemActor };
